import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalization } from "../localization";

const getRouteTitle = (pathname: string): string => {
  if (pathname === "/" || pathname === "/home") {
    return "RAUXA | Mobile-First Racing";
  }
  if (pathname === "/login") return "Log In";
  if (pathname === "/finish-signup") return "Finish account setup";
  if (pathname === "/signup") return "Sign Up";
  if (pathname === "/forgot-password") return "Reset Password";
  if (pathname === "/discover/map") return "Discover Map";
  if (pathname === "/discover") return "Discover Races";
  if (/^\/race\/[^/]+$/.test(pathname)) return "Race Details";
  if (pathname === "/live-racers") return "Live Racers";
  if (pathname === "/organize") return "Organize a Race";
  if (pathname === "/register") return "Race Registration";
  if (pathname === "/races/new") return "Create a Race";
  if (/^\/races\/[^/]+\/registered-users$/.test(pathname)) return "Registered Racers";
  if (/^\/races\/[^/]+\/results\/map$/.test(pathname)) return "Race Result Map";
  if (/^\/races\/[^/]+\/results$/.test(pathname)) return "Race Results";
  if (/^\/races\/[^/]+$/.test(pathname)) return "Race Management";
  if (pathname === "/races") return "My Races";
  if (pathname === "/account") return "Account";
  if (pathname.startsWith("/profile")) return "Profile";
  if (pathname === "/settings") return "Settings";
  if (pathname.startsWith("/admin/race-submissions/")) return "Race Submission Review";
  if (pathname === "/admin") return "Administration";
  if (pathname === "/support") return "Support";
  if (/^\/seny-i-rauxa\/?$/.test(pathname)) return "Seny i Rauxa";
  if (/^\/careers\/?$/.test(pathname)) return "Careers";
  if (pathname === "/legal/terms") return "Terms & Conditions";
  if (pathname === "/legal/privacy") return "Privacy Policy";
  if (pathname === "/legal/cookies") return "Cookie & Storage Policy";
  if (pathname === "/accessibility") return "Accessibility";
  return "RAUXA";
};

const RouteAccessibility = () => {
  const { pathname, hash } = useLocation();
  const { t } = useLocalization();
  const previousPathname = useRef(pathname);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const routeTitle = getRouteTitle(pathname);
    const localizedRouteTitle = t(routeTitle);
    document.title =
      routeTitle === "RAUXA" || routeTitle.startsWith("RAUXA |")
        ? localizedRouteTitle
        : `${localizedRouteTitle} | RAUXA`;

    if (previousPathname.current === pathname) {
      return undefined;
    }

    previousPathname.current = pathname;
    setAnnouncement(
      t("{routeTitle} page loaded", { routeTitle: localizedRouteTitle })
    );

    let animationFrame = 0;
    let observer: MutationObserver | null = null;
    let focusTimeout = 0;
    let cleanupTimeout = 0;
    const departingMain = document.getElementById("main-content");

    const focusMainContent = (allowDepartingMain = false): boolean => {
      const mainContent = document.getElementById("main-content");
      if (!mainContent || (!allowDepartingMain && mainContent === departingMain)) {
        return false;
      }

      const destination = pathname === "/legal/privacy" && hash === "#strava"
        ? document.getElementById("strava")
        : mainContent;
      if (!destination) return false;
      destination.focus({ preventScroll: true });
      destination.scrollIntoView({ block: "start" });
      return true;
    };

    observer = new MutationObserver(() => {
      if (focusMainContent()) {
        observer?.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    animationFrame = window.requestAnimationFrame(() => {
      focusMainContent();
    });
    // React may keep the previous lazy route visible during a transition. This
    // fallback runs after the click completes and focuses whichever route main
    // is current, including when React reuses the same DOM node.
    focusTimeout = window.setTimeout(() => focusMainContent(true), 100);
    cleanupTimeout = window.setTimeout(() => observer?.disconnect(), 3000);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(focusTimeout);
      window.clearTimeout(cleanupTimeout);
      observer?.disconnect();
    };
  }, [pathname, hash, t]);

  return (
    <p className="visually-hidden" aria-live="polite" aria-atomic="true">
      {announcement}
    </p>
  );
};

export default RouteAccessibility;
